import { useEffect, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { useNavigate } from 'react-router-dom';

import { useBoundStore } from 'app/store';
import { hospitalNameById } from 'shared/config';

import { I22_mock_data } from '../api/mock';
import { getReceiptTemplate } from '../lib/templateLoader';
import { useGetPayReceipt } from '../model';
import { useStatisticsReceiptDownload } from '../model/mutation';

import { ReceiptView } from './ReceiptView';
import './receipt.css';

// CSS는 조건부로 동적 로드됨
// CSS 매핑 객체
const CSS_MAP = {
  13: () => import('../ui/receiptTemplate/dsmc/dsmc.css'),
  15: () => import('../ui/receiptTemplate/hallym/hallym.css'), // 춘천 성심
  16: () => import('../ui/receiptTemplate/hallym/hallymPyeongchon.css'), // 평촌 성심
  21: () => import('../ui/receiptTemplate/hallym/hallymDongtan.css'), // 동탄 성심
  26: () => import('../ui/receiptTemplate/hallym/hallymGangnam.css'), // 강남 성심
};

export const ReceiptPage = () => {
  const navigate = useNavigate();
  const templateRef = useRef(null);
  const transformRef = useRef(null);
  const [receiptTemplate, setReceiptTemplate] = useState('');

  const { serviceParams, setParkingBarCodeData } = useBoundStore((state) => state['medicalReceipt']);
  const { hspId, mpiKey, receivePaymentId, medicalInsuranceType } = serviceParams;

  const { data: getReceiptRes } = useGetPayReceipt({
    hspId: hspId,
    mpiKey: mpiKey,
    receivePaymentId: receivePaymentId,
    medicalInsuranceType: medicalInsuranceType,
  });

  const receiptData = getReceiptRes.data;
  // const receiptData = I22_mock_data;

  useEffect(() => {
    if (hspId && templateRef.current && receiptData) {
      document.title = `${hospitalNameById[hspId]}`;

      // 조건부 CSS 로드
      const loadStylesThenSetTemplate = async () => {
        const cssLoader = CSS_MAP[hspId];
        await cssLoader();

        const template = getReceiptTemplate(hspId);
        setReceiptTemplate(template);
      };

      loadStylesThenSetTemplate();

      return () => {
        document.title = '카카오헬스케어 케어챗';
      };
    }
  }, [hspId]);

  useEffect(() => {
    if ((serviceParams, receiptData.prk_brcd_data)) {
      setParkingBarCodeData(receiptData.prk_brcd_data);
    }
  }, [serviceParams, receiptData]);

  const { mutate: statisticsReceiptDownload } = useStatisticsReceiptDownload();
  const handleDownload = async () => {
    if (templateRef.current) {
      try {
        transformRef.current.resetTransform();
        await new Promise((resolve) => setTimeout(resolve, 200));

        const canvas = await html2canvas(templateRef.current, {
          scale: 2,
          useCORS: true,
          logging: false,
          windowWidth: templateRef.current.scrollWidth,
          windowHeight: templateRef.current.scrollHeight,
          onclone: (clonedDoc) => {
            const clonedElement = clonedDoc.querySelector('.receipt-content');
            if (clonedElement) {
              clonedElement.style.height = 'auto';
              clonedElement.style.overflow = 'visible';
            }
          },
        });

        // Canvas 검증
        if (!canvas || canvas.width === 0 || canvas.height === 0) {
          throw new Error('Canvas 생성 실패');
        }

        // toDataURL로 이미지 데이터 생성 및 검증 (Blob 대신 사용)
        const dataUrl = canvas.toDataURL('image/jpeg', 1.0);

        // Data URL 검증
        if (!dataUrl || dataUrl === 'data:,') {
          throw new Error('이미지 데이터 생성 실패');
        }

        // Data URL이 올바른 형식인지 검증 (data:image/jpeg로 시작하는지)
        if (!dataUrl.startsWith('data:image/jpeg')) {
          throw new Error('이미지 데이터 형식 오류');
        }

        const link = document.createElement('a');
        link.download = '진료비 영수증.jpg';
        link.href = dataUrl;
        link.click();
        statisticsReceiptDownload({
          hspId: hspId,
          mpiKey: mpiKey,
          functionType: 'RECEIPT',
          detailFunctionType: 'DOWNLOAD',
          result: 'SUCCESS',
        });
      } catch (error) {
        statisticsReceiptDownload({
          hspId: hspId,
          mpiKey: mpiKey,
          functionType: 'RECEIPT',
          detailFunctionType: 'DOWNLOAD',
          result: 'FAIL',
        });
        console.error('영수증 다운로드 중 오류 발생:', error);
        console.error('Error details:', {
          transformRef: transformRef.current,
          templateRef: templateRef.current,
          error,
        });
      }
    }
  };

  const handleParkingBarcode = () => {
    if (receiptData && receiptData.prk_brcd_data) {
      navigate('/receipt/barcode', { state: { barcodeData: receiptData.prk_brcd_data } });
    } else {
      navigate('/receipt/barcode');
    }
  };

  return (
    <ReceiptView
      ref={templateRef}
      transformRef={transformRef}
      onDownload={handleDownload}
      onParkingBarcode={handleParkingBarcode}
      receiptData={receiptData}
      receiptTemplate={receiptTemplate}
    />
  );
};
